import { useState } from "react";
// Translation Package
import { useTranslation } from "react-i18next";

// Packages
import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { useForm } from "react-hook-form";

// Components
import PrimaryButton from "../../Buttons/Primary";
import FormInput from "../../Form/FormInput";
import Loading from "../../Utils/Loading";

import {
  DEFAULT_STATE,
  useUpdateModal,
} from "../../../Providers/FloatingModalProvider";

import { AGE_LIMIT, DATE_OF_BIRTH_FORMAT, ERROR_MESSAGES } from "../constants";
import formatDateOfBirth from "../functions/formatDateOfBirth";

import redirectAfterLoginSuccess from "Components/Auth/functions/redirectAfterLoginSuccess";
import { useUser } from "Providers/UserProvider";
import clientV2 from "lib/apollo-v2";
import { useSignInContactMutation } from "lib/apollo-v2/generated/graphql";

const VerifyBirthdateForm = ({ redirectTo, urlParams, onSuccess }) => {
  //Translation hook
  const { t } = useTranslation();

  // Modal handling
  const updateModal = useUpdateModal();
  const { refreshUser } = useUser();

  // Handle date of birth masking
  const [dateOfBirth, setDateOfBirth] = useState("");
  const handleDateOfBirth = (e) => {
    formatDateOfBirth(e.target.value, setDateOfBirth);
  };
  const handleOnDateOfBirthKeyDown = (e) => {
    const keys = {
      backspace: 8,
    };
    let val = e.target.value;

    // Detect remove
    if (e.which === keys.backspace) {
      if (val.length === 3 || val.length === 6) {
        val = val.slice(0, val.length - 1);
        setDateOfBirth(val);
      }
    }
  };

  // Mutations
  const [signInWithProvider] = useSignInContactMutation({
    client: clientV2,
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [error, setError] = useState<{ message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data) => {
    setError(null);

    const format = DATE_OF_BIRTH_FORMAT.toUpperCase();
    const dob = dayjs(data.dateOfBirth, format, true);

    const doesDateExist = dob.isValid();
    if (!doesDateExist) {
      setError({ message: t(ERROR_MESSAGES.dateOfBirth.invalidFormat) });
      return;
    }

    const today = dayjs();
    const age = today.diff(dob, "year");
    if (age < AGE_LIMIT) {
      setError({ message: t(ERROR_MESSAGES.dateOfBirth.underAge) });
      return;
    }

    const formattedBirthdate = dob.format("YYYY-MM-DD");

    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      window.location.reload();
      return;
    }

    setSubmitting(true);
    const firebaseToken = await currentUser.getIdToken();

    await signInWithProvider({
      variables: {
        firebaseToken,
        payload: {
          birthdate: formattedBirthdate,
        },
      },
    })
      .then((res) => res.data.result.jwtToken)
      .then(async (jwtToken) => {
        if (!jwtToken) {
          setError({ message: t(ERROR_MESSAGES.unknown) });
          return;
        }
        // Set token
        localStorage.setItem("token", jwtToken);

        if (onSuccess) {
          updateModal(DEFAULT_STATE);
          await refreshUser();
          onSuccess();
        } else {
          updateModal(DEFAULT_STATE);
          // Redirect
          redirectAfterLoginSuccess(redirectTo, urlParams);
        }
      })
      .catch((error) => {
        setError(error);
      });

    setSubmitting(false);
  };

  return (
    <div className="auth-form">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="auth-form__verify-email"
      >
        <div className="row">
          <div className="col-12 ">
            <h6 className="auth-form__form-title">{t("date_of_birth")}</h6>
          </div>
        </div>
        {/* EMAIL */}
        <div className="row">
          <div className="col-12 verify-email-text">
            <p>
              {t("error_birthdate_missing")} <br />
              {t("date_of_birth_notice")}
            </p>
          </div>
          {/* Verify code */}
          <div className="col-12 mt-2">
            <FormInput
              register={register("dateOfBirth", {
                required: true,
                minLength: 10,
              })}
              inputMode="numeric"
              type="text"
              name="dateOfBirth"
              label={t("date_of_birth")}
              value={dateOfBirth}
              onChange={handleDateOfBirth}
              onKeyDown={handleOnDateOfBirthKeyDown}
              forceLabelToFloat={true}
              error={errors.dateOfBirth || error}
              placeholder={DATE_OF_BIRTH_FORMAT}
              autoComplete="off"
            />
          </div>

          <div className="col-12">
            <div className="verify-code-button mt-3">
              <PrimaryButton
                label={submitting ? <Loading light /> : t("continue")}
                disabled={submitting}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VerifyBirthdateForm;
